import { useState, useEffect } from 'react';
import { fetchGoogleFonts, loadGoogleFont, type GoogleFont } from './lib/google-fonts';
import { fontToDots } from './lib/font2dots-canvas';
import './App.css';

const SAMPLE_TEXTS = [
  '龍が如く',
  '麒麟が来る',
  '麒麟鳳凰',
  '鬱金香',
  '曖昧模糊',
  '彌縫策',
  '攒撃',
  '饗宴',
  '龍驤虎視',
  '麟鳳亀龍',
  '彌栄',
  '攒三聚五',
  '饗応',
  '麓山',
  '鱗爪',
];

function updateURL(params: Record<string, string>) {
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
  });
  window.history.replaceState({}, '', url.toString());
}

function getURLParam(key: string): string {
  const params = new URLSearchParams(window.location.search);
  return params.get(key) || '';
}

function App() {
  const [fonts, setFonts] = useState<GoogleFont[]>([]);
  const [selectedFont, setSelectedFont] = useState<string>('Noto Sans JP');
  const [text, setText] = useState<string>('');
  const [outputLines, setOutputLines] = useState<number>(12);
  const [strokeWidth, setStrokeWidth] = useState<number>(50);
  const [horizontalLetterSpacing, setHorizontalLetterSpacing] = useState<number>(-2);
  const [verticalLetterSpacing, setVerticalLetterSpacing] = useState<number>(1);
  const [brailleOutput, setBrailleOutput] = useState<string>('');
  const [editableOutput, setEditableOutput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [fontsLoading, setFontsLoading] = useState<boolean>(true);
  const [fontLoaded, setFontLoaded] = useState<boolean>(false);
  const [isVertical, setIsVertical] = useState<boolean>(false);

  useEffect(() => {
    loadFonts();
    
    const urlText = getURLParam('text');
    const urlFont = getURLParam('font');
    const urlLines = getURLParam('lines');
    const urlWidth = getURLParam('width');
    const urlSpacing = getURLParam('spacing');
    const urlVertical = getURLParam('vertical');
    const isVerticalMode = urlVertical === 'true';

    if (urlText) {
      setText(urlText);
    } else {
      const randomText = SAMPLE_TEXTS[Math.floor(Math.random() * SAMPLE_TEXTS.length)];
      setText(randomText);
    }

    if (urlFont) {
      setSelectedFont(urlFont);
    }
    if (urlLines) {
      setOutputLines(Number(urlLines));
    }
    if (urlWidth) {
      setStrokeWidth(Number(urlWidth));
    }
    if (urlSpacing) {
      if (isVerticalMode) {
        setVerticalLetterSpacing(Number(urlSpacing));
      } else {
        setHorizontalLetterSpacing(Number(urlSpacing));
      }
    }
    if (urlVertical === 'true') {
      setIsVertical(true);
    }
  }, []);

  useEffect(() => {
    updateURL({
      text: text,
      font: selectedFont,
      lines: String(outputLines),
      width: String(strokeWidth),
      spacing: isVertical ? String(verticalLetterSpacing) : String(horizontalLetterSpacing),
      vertical: String(isVertical),
    });
  }, [selectedFont, text, outputLines, strokeWidth, horizontalLetterSpacing, verticalLetterSpacing, isVertical]);

  useEffect(() => {
    if (fontLoaded && text) {
      generateBraille();
    }
  }, [selectedFont, text, outputLines, strokeWidth, horizontalLetterSpacing, verticalLetterSpacing, fontLoaded, isVertical]);

  const loadFonts = async () => {
    try {
      setFontsLoading(true);
      const googleFonts = await fetchGoogleFonts();
      setFonts(googleFonts);
      await loadGoogleFont('Noto Sans JP');
      setFontLoaded(true);
    } catch (error) {
      console.error('Failed to load fonts:', error);
    } finally {
      setFontsLoading(false);
    }
  };

  const handleFontChange = async (fontName: string) => {
    setSelectedFont(fontName);
    setFontLoaded(false);
    try {
      await loadGoogleFont(fontName);
      setFontLoaded(true);
    } catch (error) {
      console.error('Failed to load font:', error);
    }
  };

  const generateBraille = async () => {
    if (!fontLoaded || !text) return;

    setLoading(true);
    try {
      const fontSize = outputLines * 3;
      const threshold = Math.floor(50 + (strokeWidth * 1.5));
      const result = await fontToDots.textToDots(selectedFont, text, fontSize, threshold, isVertical, horizontalLetterSpacing);

      if (result.brailleColumns) {
        setBrailleOutput('');
        // 縦書き: 各文字を縦に並べる（上部の空行を削除）
        const trimmedColumns = result.brailleColumns.map(col => {
          // 上部の空行を削除（空白点字のみの行）
          let startIndex = 0;
          for (let i = 0; i < col.length; i++) {
            if ([...col[i]].some(char => char !== '⠀')) {
              startIndex = i;
              break;
            }
          }
          return col.slice(startIndex);
        });
        const paddedColumns = trimmedColumns.map(col => {
          const maxWidth = Math.max(...col.map(line => [...line].length));
          return col.map(line => {
            const lineLength = [...line].length;
            return line + '⠀'.repeat(maxWidth - lineLength);
          });
        });
        setEditableOutput(paddedColumns.map(col => col.join('\n')).join('\n\n'));
      } else {
        setBrailleOutput(result.brailleLines.join('\n'));
        // 横書き: 左端の空白列を削除
        const lines = result.brailleLines;
        // 各行で最初の非空白文字の位置を探す
        let minLeadingSpaces = Infinity;
        for (const line of lines) {
          const chars = [...line];
          for (let i = 0; i < chars.length; i++) {
            if (chars[i] !== '⠀') {
              minLeadingSpaces = Math.min(minLeadingSpaces, i);
              break;
            }
          }
        }
        if (minLeadingSpaces === Infinity) minLeadingSpaces = 0;
        // 左端の空白を削除
        const trimmedLines = lines.map(line => {
          const chars = [...line];
          return chars.slice(minLeadingSpaces).join('');
        });
        // 各行の長さを揃える
        const maxLength = Math.max(...trimmedLines.map(line => [...line].length));
        const paddedLines = trimmedLines.map(line => {
          const lineLength = [...line].length;
          return line + '⠀'.repeat(maxLength - lineLength);
        });
        setEditableOutput(paddedLines.join('\n'));
      }
    } catch (error) {
      console.error('Failed to generate braille:', error);
      setBrailleOutput('');
      setEditableOutput('');
      const sampleText = SAMPLE_TEXTS[Math.floor(Math.random() * SAMPLE_TEXTS.length)];
      setText(sampleText);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(editableOutput);
  };

  const shareTwitter = () => {
    const url = encodeURIComponent(window.location.href);
    const maxLength = 140;
    
    let shareText = `【${text}】を点字アートに変換しました！`;
    
    if (brailleOutput && brailleOutput.length <= 50) {
      shareText += `\n${brailleOutput}`;
    } else if (brailleOutput) {
      shareText += `\n${brailleOutput.substring(0, 47)}...`;
    }
    
    shareText += '\n#Font2Dots #点字アート #JapaneseBrailleArt';
    
    if (shareText.length > maxLength) {
      shareText = shareText.substring(0, maxLength - 3) + '...';
    }
    
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${url}`, '_blank');
  };

  const shareFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareLine = () => {
    const url = encodeURIComponent(window.location.href);
    const shareText = encodeURIComponent(`【${text}】を点字アートに変換しました！`);
    window.open(`https://social-plugins.line.me/lineit/share?url=${url}&text=${shareText}`, '_blank');
  };

  const copyURL = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('URLをコピーしました！');
  };

  return (
    <div className="app">
      <header className="header">
        <h1>Font2Dots</h1>
        <p>Google Fontsからフォントを選んで点字を生成</p>
      </header>

      <main className="main">
        <div className="controls">
          <div className="controls-grid">
            <div className="control-group">
              <label htmlFor="font-select">フォント</label>
              <select
                id="font-select"
                value={selectedFont}
                onChange={(e) => handleFontChange(e.target.value)}
                disabled={fontsLoading}
              >
                {fontsLoading ? (
                  <option>ロード中...</option>
                ) : (
                  fonts.map((font) => (
                    <option key={font.family} value={font.family}>
                      {font.family}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="control-group">
              <label htmlFor="text-input">文字</label>
              <input
                id="text-input"
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>

            <div className="control-group">
              <label htmlFor="output-lines">出力行数: {outputLines}行</label>
              <input
                id="output-lines"
                type="range"
                min="5"
                max="50"
                value={outputLines}
                onChange={(e) => setOutputLines(Number(e.target.value))}
              />
            </div>

            <div className="control-group">
              <label htmlFor="stroke-width">文字の太さ: {strokeWidth}%</label>
              <input
                id="stroke-width"
                type="range"
                min="0"
                max="100"
                value={strokeWidth}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
              />
            </div>

            <div className="control-group">
              <label htmlFor="letter-spacing">文字間隔: {isVertical ? `${verticalLetterSpacing}字分` : `${horizontalLetterSpacing}px`}</label>
              <input
                id="letter-spacing"
                type="range"
                min={isVertical ? "-3" : "-10"}
                max={isVertical ? "5" : "20"}
                step="1"
                value={isVertical ? verticalLetterSpacing : horizontalLetterSpacing}
                onChange={(e) => isVertical ? setVerticalLetterSpacing(Number(e.target.value)) : setHorizontalLetterSpacing(Number(e.target.value))}
              />
            </div>

            <div className="control-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={isVertical}
                  onChange={(e) => setIsVertical(e.target.checked)}
                />
                <span>縦書き</span>
              </label>
            </div>
          </div>
        </div>

        <div className="output">
          <h2>出力{loading && ' (生成中...)'}</h2>
          {text && (
            <div className="font-preview">
              <span className="font-preview-label">フォント表示:</span>
              <span 
                className="font-preview-text" 
                style={{ 
                  fontFamily: `"${selectedFont}", sans-serif`,
                  writingMode: isVertical ? 'vertical-lr' : 'horizontal-tb',
                  textOrientation: 'upright'
                }}
              >
                {text}
              </span>
            </div>
          )}
          <textarea
            className="braille-output-textarea"
            value={editableOutput}
            onChange={(e) => setEditableOutput(e.target.value)}
            placeholder="ここに点字が表示されます"
            rows={editableOutput.split('\n').length || 5}
          />
          {editableOutput && (
            <div className="output-actions">
              <div className="action-buttons">
                <button className="copy-button" onClick={copyToClipboard}>
                  点字をコピー
                </button>
                <button className="url-button" onClick={copyURL}>
                  URLをコピー
                </button>
              </div>
              <div className="share-buttons">
                <span className="share-label">SNSで共有:</span>
                <button className="share-button twitter" onClick={shareTwitter}>
                  X
                </button>
                <button className="share-button facebook" onClick={shareFacebook}>
                  f
                </button>
                <button className="share-button line" onClick={shareLine}>
                  L
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;
